import { Link, useLocation } from "react-router-dom";
import { Compass, Map as MapIcon, QrCode, BookHeart, Car } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useMode } from "../lib/appMode";

/**
 * BottomNav — iter95 reordered.
 *
 * Single bottom row reused on every mobile route (and reused as the bottom
 * half of /map's two-row MapBottomBar).
 *
 * 5 items in order: DISCOVER · MAP · QR (contained gold disc) · RIDE · JOURNEY.
 *
 * Profile is intentionally NOT in the bottom nav — it lives in the header
 * avatar dropdown (iter90).
 *
 * Props:
 *   - bare (default false): when true, render only the 5-column grid (no fixed
 *     wrapper). Used by MapBottomBar to embed the row inside its own container.
 */
const NAV_ITEMS = [
  { to: "/",         icon: Compass,   labelKey: "nav_discover", testid: "nav-discover" },
  { to: "/map",      icon: MapIcon,   labelKey: "nav_map",      testid: "nav-map" },
  null, // QR — center slot, contained gold disc
  { to: "/car/map",  icon: Car,       labelKey: "nav_ride",     testid: "nav-ride" },
  { to: "/journey",  icon: BookHeart, labelKey: "nav_journey",  testid: "nav-journey" },
];

export function BottomNav({ bare = false } = {}) {
  const [mode] = useMode();
  // iter158 — When the user is in Ride Mode, the Discover-style bottom
  // nav must be fully absent from the DOM so no popup or modal can
  // bleed it through the Ride flow (active ride, review, ride
  // confirmation, ride mode home). Reactively hides when mode flips.
  if (mode === "ride") return null;
  const row = <NavRow />;
  if (bare) return row;
  return (
    <nav
      data-testid="bottom-nav"
      className="fixed bottom-0 inset-x-0"
      style={{
        background: "#0d0d0d",
        borderTop: "0.5px solid rgba(232,184,75,0.2)",
        paddingBottom: "env(safe-area-inset-bottom, 0)",
        zIndex: 20,
      }}
    >
      {row}
    </nav>
  );
}

function NavRow() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  return (
    <div
      data-testid="bottom-nav-row"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
        alignItems: "center",
        padding: "16px 0 22px",
      }}
    >
      {NAV_ITEMS.map((item) => {
        if (item === null) {
          return (
            <Link
              key="qr"
              to="/scan"
              data-testid="nav-scan"
              aria-label={t("nav_qr")}
              className="flex flex-col items-center"
              style={{ gap: 7 }}
            >
              <span
                className="grid place-items-center rounded-full"
                style={{
                  width: 60,
                  height: 60,
                  background: "#E8B84B",
                  marginTop: -22,
                  boxShadow: "0 6px 20px rgba(232,184,75,0.45)",
                }}
              >
                <QrCode style={{ width: 24, height: 24, color: "#0B0B0B" }} strokeWidth={2.25} />
              </span>
              <span
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14,
                  color: "#E8B84B",
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                }}
              >
                {t("nav_qr")}
              </span>
            </Link>
          );
        }
        const active = item.to === "/"
          ? pathname === "/"
          : pathname.startsWith(item.to);
        const Icon = item.icon;
        return (
          <Link
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            className="flex flex-col items-center"
            style={{ minHeight: 48, justifyContent: "center", gap: 7 }}
          >
            <Icon
              style={{
                width: 24,
                height: 24,
                color: active ? "#E8B84B" : "#FFFFFF",
                filter: active ? "drop-shadow(0 0 10px rgba(200,160,99,0.45))" : undefined,
                transition: "color 0.2s ease",
              }}
              strokeWidth={1.5}
            />
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 14,
                color: active ? "#E8B84B" : "#FFFFFF",
                fontWeight: 500,
                letterSpacing: "0.04em",
              }}
            >
              {t(item.labelKey)}
            </span>
          </Link>
        );
      })}
    </div>
  );
}
